import { useEffect, useState } from 'react';
import { logger } from './logger';
import { RECORDER_STATUS_EVENT, isRecorderBusy } from './recorderEvents';

// Cache busting utilities
export class CacheUtils {
    // Voeg een timestamp toe aan asset URLs om caching te voorkomen
    static addCacheBuster(url: string): string {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}v=${Date.now()}`;
    }

    // Voeg een versie parameter toe gebaseerd op build tijd
    static addVersion(url: string, version?: string): string {
        const buildVersion = version || process.env.REACT_APP_VERSION || '1.0.0';
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}v=${buildVersion}`;
    }

    // Clear alle browser caches programmatisch
    static async clearCaches(): Promise<void> {
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
            );
            logger.log('All caches cleared');
        }
    }

    // Check voor nieuwe versie en refresh als nodig
    static async checkForUpdates(): Promise<boolean> {
        try {
            const response = await fetch('/version.json', {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const currentVersion = localStorage.getItem('app-version');

                if (currentVersion && currentVersion !== data.version) {
                    // Nieuwe versie gevonden
                    await this.clearCaches();
                    localStorage.setItem('app-version', data.version);
                    return true;
                }

                if (!currentVersion) {
                    localStorage.setItem('app-version', data.version);
                }
            }
        } catch (error) {
            logger.warn('Could not check for updates:', error);
        }

        return false;
    }

    // Force een hard refresh
    static forceRefresh(): void {
        window.location.reload();
    }

    // Soft refresh - alleen nieuwe resources laden
    static async softRefresh(): Promise<void> {
        await this.clearCaches();

        // Trigger een re-render van de app
        window.dispatchEvent(new CustomEvent('app-refresh'));
    }
}

// Hook voor React componenten
export function useVersionCheck(checkInterval: number = 30000) {
    const [updateAvailable, setUpdateAvailable] = useState(false);

    useEffect(() => {
        // Alleen version checking in productie
        if (process.env.NODE_ENV !== 'production') {
            return;
        }

        let cancelled = false;

        const checkForUpdates = async () => {
            // Niets doen zolang de recorder bezig is: checkForUpdates() wist de
            // caches en zet de banner (met aftelling naar een reload) aan. Beide
            // mogen een lopende opname niet raken; we kijken later opnieuw.
            if (isRecorderBusy()) {
                return;
            }

            const hasUpdate = await CacheUtils.checkForUpdates();
            // Eenmaal gevonden blijft de update staan: de versie is inmiddels in
            // localStorage bijgewerkt, dus een volgende check meldt hem niet meer.
            if (!cancelled && hasUpdate) {
                setUpdateAvailable(true);
            }
        };

        // Check bij startup
        void checkForUpdates();

        // Check periodiek
        const interval = setInterval(checkForUpdates, checkInterval);

        // Zodra de opname klaar is meteen kijken, niet pas bij de volgende tick
        const handleRecorderStatus = () => {
            if (!isRecorderBusy()) {
                void checkForUpdates();
            }
        };
        window.addEventListener(RECORDER_STATUS_EVENT, handleRecorderStatus);

        return () => {
            cancelled = true;
            clearInterval(interval);
            window.removeEventListener(RECORDER_STATUS_EVENT, handleRecorderStatus);
        };
    }, [checkInterval]);

    const applyUpdate = () => {
        // Vangnet: bijwerken betekent herladen, dus nooit tijdens een opname
        if (isRecorderBusy()) {
            return;
        }
        CacheUtils.forceRefresh();
    };

    return { updateAvailable, applyUpdate };
}

// Development utilities
export class DevCacheUtils {
    // Clear alle browser caches en localStorage voor development
    static async clearAllDevelopmentData(): Promise<void> {
        if (process.env.NODE_ENV !== 'development') {
            logger.warn('clearAllDevelopmentData should only be used in development');
            return;
        }

        // Clear alle caches
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
            );
            logger.log('All caches cleared for development');
        }

        // Clear localStorage items gerelateerd aan app
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.startsWith('app-') || key.startsWith('notizy-'))) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));

        logger.log('Development data cleared');
    }

    // Disable alle caching mechanismen voor development
    static setupDevelopmentMode(): void {
        if (process.env.NODE_ENV !== 'development') {
            return;
        }

        // Voeg cache buster toe aan alle fetch requests
        const originalFetch = window.fetch;
        window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
            const url = typeof input === 'string' ? input : input.toString();
            const cacheBustedUrl = CacheUtils.addCacheBuster(url);
            return originalFetch(cacheBustedUrl, {
                ...init,
                cache: 'no-cache'
            });
        };

        logger.log('Development mode: caching disabled');
    }
} 